import React, { useEffect, useState } from "react";
import {
  Search,
  Plus,
  MapPin,
  Laptop,
  Smartphone,
  Tablet,
  Headphones,
  Gamepad2,
  Package,
  Sparkles,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchMarketPlaceItems } from "../../api/marketPlace.js";

const CATEGORIES = [
  { name: "All", icon: Sparkles },
  { name: "Laptops", icon: Laptop },
  { name: "Phones", icon: Smartphone },
  { name: "Tablets", icon: Tablet },
  { name: "Accessories", icon: Package },
  { name: "Gaming", icon: Gamepad2 },
  { name: "Audio", icon: Headphones },
];

function MarketPlace() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // High quality campus gadget mock listings
  const mockMarketplaceItems = [
    {
      _id: "mkt_1",
      title: "Apple MacBook Air M1 (8GB / 256GB SSD) - Space Gray",
      description:
        "Selling in excellent condition. Battery health 88%. Comes with original 30W charger, box, and bill. Perfect for coding and college projects.",
      price: 54999,
      category: "Laptops",
      condition: "Like New",
      location: "Bansal Institute, Lucknow",
      imageUrl:
        "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=800&q=80",
      createdAt: "Aug 28, 2026",
      owner: { username: "Aditya Rai", contact: "9876543210" },
    },
    {
      _id: "mkt_2",
      title: "iPhone 13 (128GB) - Starlight White",
      description:
        "1 year old, scratchless screen with tempered glass and Spigen case. Battery health 86%. Never repaired.",
      price: 39500,
      category: "Phones",
      condition: "Excellent",
      location: "Hostel Block B, DU South Campus",
      imageUrl:
        "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?auto=format&fit=crop&w=800&q=80",
      createdAt: "Aug 26, 2026",
      owner: { username: "Rohan Verma", contact: "9123456789" },
    },
    {
      _id: "mkt_3",
      title: "Sony WH-1000XM4 Wireless Noise-Canceling Headphones",
      description:
        "Top-tier Active Noise Cancellation headphones. Works perfectly for quiet library study sessions. Includes original carrying case.",
      price: 14500,
      category: "Audio",
      condition: "Good",
      location: "BITS Pilani, Campus Market",
      imageUrl:
        "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=800&q=80",
      createdAt: "Aug 24, 2026",
      owner: { username: "Priya Sharma", contact: "9988776655" },
    },
    {
      _id: "mkt_4",
      title: "iPad Air 5th Gen (64GB Wi-Fi) + Apple Pencil 2",
      description:
        "M1 iPad Air with Apple Pencil 2 included. Ideal for digital note-taking, sketching, and online lectures.",
      price: 42000,
      category: "Tablets",
      condition: "Like New",
      location: "IIT Bombay, Hostel 12",
      imageUrl:
        "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?auto=format&fit=crop&w=800&q=80",
      createdAt: "Aug 20, 2026",
      owner: { username: "Vikram Singh", contact: "9811223344" },
    },
  ];

  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await fetchMarketPlaceItems();
      if (Array.isArray(data) && data.length > 0) {
        setItems([...data, ...mockMarketplaceItems]);
      } else {
        setItems(mockMarketplaceItems);
      }
    } catch (error) {
      console.warn("Backend API unavailable, fallback to mock gadget items");
      setItems(mockMarketplaceItems);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filteredItems = items.filter((item) => {
    const matchesCategory =
      selectedCategory === "All" || item.category === selectedCategory;
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description &&
        item.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50/70 text-slate-800 pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 text-white py-12 px-4 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
            <div>
              <span className="inline-flex items-center space-x-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-semibold text-purple-200 mb-3 border border-white/10">
                <ShieldCheck size={15} />
                <span>Verified Peer-to-Peer Campus Trading</span>
              </span>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                Student Gadget MarketPlace
              </h1>
              <p className="text-sm sm:text-base text-purple-100 mt-1.5 font-medium max-w-xl">
                Buy, sell, and exchange laptops, phones, headphones, and study accessories directly with fellow students.
              </p>
            </div>

            <button
              onClick={() => navigate("/marketplace/uploadItem")}
              className="flex items-center justify-center space-x-2 px-6 py-3.5 bg-white text-purple-900 rounded-2xl font-bold text-sm shadow-lg hover:bg-purple-50 transition-all transform hover:-translate-y-0.5 shrink-0"
            >
              <Plus size={20} className="text-purple-700" />
              <span>Sell Your Gadget</span>
            </button>
          </div>

          {/* Search Input Bar */}
          <div className="relative max-w-2xl">
            <Search
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search for MacBook, iPhone, headphones, tablets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white text-gray-900 placeholder-gray-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-400 shadow-md transition"
            />
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 space-y-6">
        {/* Category Pills Bar */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2 no-scrollbar">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isSelected = selectedCategory === cat.name;
            return (
              <button
                key={cat.name}
                onClick={() => setSelectedCategory(cat.name)}
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all shrink-0 ${
                  isSelected
                    ? "bg-purple-600 text-white shadow-xs"
                    : "bg-white text-gray-700 border border-gray-100 hover:bg-gray-100"
                }`}
              >
                <Icon size={14} />
                <span>{cat.name}</span>
              </button>
            );
          })}
        </div>

        {/* Stats Summary Bar */}
        <div className="flex items-center justify-between text-xs font-semibold text-gray-500 px-1">
          <span>
            Available Gadgets: <strong className="text-purple-700">{filteredItems.length}</strong>
          </span>
          <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full border border-purple-100">
            Category: {selectedCategory}
          </span>
        </div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredItems.map((item) => (
            <div
              key={item._id}
              onClick={() => navigate(`/marketplace/viewItem/${item._id}`)}
              className="group bg-white rounded-2xl border border-gray-100/90 overflow-hidden shadow-xs hover:shadow-md hover:border-purple-200 transition-all duration-300 cursor-pointer flex flex-col justify-between"
            >
              <div>
                {/* Image Box */}
                <div className="relative w-full h-48 bg-gray-100 overflow-hidden">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md px-2.5 py-1 rounded-md text-[11px] font-bold text-purple-700 shadow-xs border border-white">
                    {item.category}
                  </div>
                  <div className="absolute top-3 right-3 bg-amber-500 text-white px-2.5 py-1 rounded-md text-[11px] font-bold shadow-xs">
                    {item.condition}
                  </div>
                </div>

                {/* Info Content */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 text-sm group-hover:text-purple-700 transition-colors line-clamp-2 mb-2 leading-snug">
                    {item.title}
                  </h3>

                  <div className="text-lg font-extrabold text-emerald-600 mb-3">
                    ₹{item.price ? item.price.toLocaleString("en-IN") : "0"}
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
                    <span className="flex items-center space-x-1 truncate max-w-[60%]">
                      <MapPin size={13} className="text-gray-400 shrink-0" />
                      <span className="truncate">
                        {item.location ? item.location.split(",")[0] : "Campus"}
                      </span>
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {item.createdAt ? item.createdAt.split("T")[0] : "Recent"}
                    </span>
                  </div>
                </div>
              </div>

              {/* View Action Footer */}
              <div className="px-4 pb-4">
                <button className="w-full py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 font-bold text-xs rounded-xl transition-colors flex items-center justify-center space-x-1">
                  <span>View Details</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-bold text-gray-800 text-lg mb-1">
              No Gadgets Found
            </h3>
            <p className="text-xs text-gray-500 mb-4 max-w-sm mx-auto">
              No items match your search or selected category. Try selecting another category or list your own item!
            </p>
            <button
              onClick={() => {
                setSelectedCategory("All");
                setSearchQuery("");
              }}
              className="px-5 py-2 bg-purple-600 text-white rounded-xl text-xs font-bold shadow-xs hover:bg-purple-700 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MarketPlace;
